import React, { useEffect, useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import {
  TodoEditFeedbackPopover_todo$key,
  TodoFeedbackStatus,
} from "./__generated__/TodoEditFeedbackPopover_todo.graphql";
import {
  AHButton,
  AHIcon,
  AHText,
  Box,
  Dialog,
  Input,
  TouchableBox,
} from "arkh";
import { TodoEditFeedbackPopover_sendFeedbackMutation } from "./__generated__/TodoEditFeedbackPopover_sendFeedbackMutation.graphql";
import cuid from "cuid";

import { PopoverButton } from "@/src/common/PopoverButton";
import { FeedbackStatusItem } from "widgets/src/Todo/TodoContact";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { TodoEditFeedbackPopoverSendFeedbackForm_todo$key } from "./__generated__/TodoEditFeedbackPopoverSendFeedbackForm_todo.graphql";
import { TodoEditFeedbackPopoverFeedbackHistory_todoFeedback$key } from "./__generated__/TodoEditFeedbackPopoverFeedbackHistory_todoFeedback.graphql";
import { TodoTimelineFeedback } from "widgets/src/Todo/TodoTimeline/TodoTimelineFeedback";
import { ScrollView } from "react-native";
import { ActionFooter } from "@/src/components/ActionFooter";
import { FormattedMessage, useIntl } from "react-intl";
import {
  KeyboardAvoidingView,
  KeyboardAvoidingViewProps,
} from "react-native-keyboard-controller";

function KAV(props: KeyboardAvoidingViewProps) {
  let p = { ...props };
  p.behavior = "padding";
  return <KeyboardAvoidingView {...p} />;
}
function Header(props: { feedbackStatus: TodoFeedbackStatus; todoId: string }) {
  const [updateFeedbackStatus] = useMutation(graphql`
    mutation TodoEditFeedbackPopover_updateFeedbackStatusMutation(
      $todoId: ID!
      $employeeId: ID
      $newFeedbackStatus: TodoFeedbackStatus!
    ) {
      todo(todoId: $todoId, employeeId: $employeeId) {
        updateFeedbackStatus(newFeedbackStatus: $newFeedbackStatus) {
          id
          feedbackStatus
          allTodoFeedbacks {
            ...TodoEditFeedbackPopoverFeedbackHistory_todoFeedback
          }

          ...TodoEditTimeline_todo @defer
        }
      }
    }
  `);
  const { currentEmployeeId } = usePersistedEmployeeId();

  function onPress(newFeedbackStatus: TodoFeedbackStatus) {
    updateFeedbackStatus({
      variables: {
        todoId: props.todoId,
        employeeId: currentEmployeeId,
        newFeedbackStatus,
      },
      optimisticUpdater: (store) => {
        store.get(props.todoId)?.setValue(newFeedbackStatus, "feedbackStatus");
      },
    });
  }
  return (
    <PopoverButton
      popoverWidth={230}
      popoverContent={(onClose) => (
        <Box
          space="small"
          style={{ flexDirection: "column", justifyContent: "flex-start" }}
          gap="small"
        >
          <TouchableBox
            onPress={() => {
              onPress("FEEDBACK_COMPLETED");
              onClose();
            }}
          >
            <FeedbackStatusItem feedbackStatus={"FEEDBACK_COMPLETED"} />
          </TouchableBox>
          <TouchableBox
            onPress={() => {
              onPress("FEEDBACK_NEEDED");
              onClose();
            }}
          >
            <FeedbackStatusItem feedbackStatus={"FEEDBACK_NEEDED"} />
          </TouchableBox>
          <TouchableBox
            onPress={() => {
              onPress("FEEDBACK_DISMISSED");
              onClose();
            }}
          >
            <FeedbackStatusItem feedbackStatus={"FEEDBACK_DISMISSED"} />
          </TouchableBox>
        </Box>
      )}
    >
      {(open) => (
        <TouchableBox onPress={open}>
          <FeedbackStatusItem
            feedbackStatus={props.feedbackStatus}
            left={<AHIcon type="chevron-down" color="washedText" />}
          />
        </TouchableBox>
      )}
    </PopoverButton>
  );
}

export function TodoEditFeedbackPopover(props: {
  todo: TodoEditFeedbackPopover_todo$key;
  visible: boolean;
  onClose: () => void;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoEditFeedbackPopover_todo on Todo {
        id
        title
        feedbackStatus
        ...TodoEditFeedbackPopoverSendFeedbackForm_todo

        allTodoFeedbacks {
          ...TodoEditFeedbackPopoverFeedbackHistory_todoFeedback
        }
      }
    `,
    props.todo
  );

  const feedBackLength = todo.allTodoFeedbacks?.length ?? 0;

  const [forceNewReply, setForceNewReply] = useState(false);
  useEffect(() => {
    if (feedBackLength > 0) {
      setForceNewReply(false);
    }
  }, [feedBackLength]);

  return (
    <Dialog
      visible={props.visible}
      onClose={props.onClose}
      minHeight={400}
      maxHeight={600}
      view={KAV}
      customTitle={
        todo.feedbackStatus ? (
          <Header feedbackStatus={todo.feedbackStatus} todoId={todo.id} />
        ) : null
      }
    >
      {feedBackLength > 0 && !forceNewReply ? (
        <FeedbackHistory
          todo={todo.allTodoFeedbacks ?? []}
          onRequestNewReply={() => setForceNewReply(true)}
        />
      ) : (
        <SendFeedbackForm
          todo={todo}
          onRequestClose={() => {
            props.onClose();
            setForceNewReply(false);
          }}
        />
      )}
    </Dialog>
  );
}

function FeedbackHistory(props: {
  todo: TodoEditFeedbackPopoverFeedbackHistory_todoFeedback$key;
  onRequestNewReply: () => void;
}) {
  const feedbacks = useFragment(
    graphql`
      fragment TodoEditFeedbackPopoverFeedbackHistory_todoFeedback on TodoFeedback
      @relay(plural: true) {
        id
        body
        ...TodoTimelineFeedback_feedback
      }
    `,
    props.todo
  );

  return (
    <>
      <ScrollView style={{ flex: 1 }}>
        <Box space="medium" gap="medium">
          {feedbacks.map((feedback, i) => (
            <Box key={feedback.id}>
              <TodoTimelineFeedback feedback={feedback} />
            </Box>
          ))}
        </Box>
      </ScrollView>
      <Box
        spaceVertical="medium"
        spaceHorizontal="medium"
        style={{ flexDirection: "row", justifyContent: "flex-end" }}
      >
        <ActionFooter.Primary
          title={
            <FormattedMessage
              defaultMessage="New reply"
              description="TodoEditFeedbackPopover:NewReplyButton:title"
            />
          }
          icon="add"
          onPress={props.onRequestNewReply}
        />
      </Box>
    </>
  );
}

function SendFeedbackForm(props: {
  todo: TodoEditFeedbackPopoverSendFeedbackForm_todo$key;
  onRequestClose: () => void;
}) {
  const { currentEmployeeId } = usePersistedEmployeeId();

  const todo = useFragment(
    graphql`
      fragment TodoEditFeedbackPopoverSendFeedbackForm_todo on Todo {
        id
        title
        feedbackStatus
        contact {
          name
          email
          phoneNumber
        }
        ...TodoContact_todo
        allTodoFeedbacks {
          body
        }
      }
    `,
    props.todo
  );
  const userPlacehodler = todo.contact?.name ? ` ${todo.contact.name}` : "";
  const titlePlaceholder = `Re: ${todo.title}${userPlacehodler}`;
  const [title, setTitle] = useState(titlePlaceholder);
  const [body, setBody] = useState("");
  const [idempotencyKey, _setIdempotencyKey] = useState(cuid());

  function resetState() {
    setTitle(titlePlaceholder);
    setBody("");
    _setIdempotencyKey(cuid());
  }

  const [sendFeedback, loading] =
    useMutation<TodoEditFeedbackPopover_sendFeedbackMutation>(graphql`
      mutation TodoEditFeedbackPopover_sendFeedbackMutation(
        $title: String!
        $body: String
        $todoId: ID!
        $employeeId: ID
        $idempotencyKey: String!
      ) {
        todo(todoId: $todoId, employeeId: $employeeId) {
          sendFeedback(
            input: { title: $title, body: $body }
            idempotencyKey: $idempotencyKey
          ) {
            todo {
              id
              feedbackStatus
              allTodoFeedbacks {
                body
              }
              ...TodoEditTimeline_todo @defer
            }
          }
        }
      }
    `);

  const intl = useIntl();

  return (
    <>
      <Box
        spaceHorizontal="medium"
        spaceTop="small"
        gap="medium"
        style={{ flex: 1 }}
      >
        <Box gap="xxsmall">
          {todo.contact?.name ? (
            <AHText type="info-small">{todo.contact.name}</AHText>
          ) : null}
          {todo.contact?.email ? (
            <AHText type="info-small" color="text-secondary">
              {todo.contact?.email}
            </AHText>
          ) : null}
          {todo.contact?.phoneNumber ? (
            <AHText type="info-small" color="text-secondary">
              {todo.contact?.phoneNumber}
            </AHText>
          ) : null}
        </Box>
        <Input
          placeholder={intl.formatMessage({
            defaultMessage: "Subject",
            description: "TodoEditFeedbackPopover:SubjectPlaceholder",
          })}
          numberOfLines={1}
          value={title}
          onChangeText={setTitle}
        />
        <Input
          placeholder={intl.formatMessage({
            defaultMessage: "Message",
            description: "TodoEditFeedbackPopover:MessagePlaceholder",
          })}
          numberOfLines={3}
          multiline
          value={body}
          onChangeText={setBody}
        />
      </Box>
      <Box
        space="medium"
        style={{ flexDirection: "row", justifyContent: "flex-end" }}
      >
        <AHButton
          loading={loading}
          disabled={loading}
          themeType="primary"
          onPress={() => {
            sendFeedback({
              variables: {
                title,
                body,
                idempotencyKey: idempotencyKey,
                todoId: todo.id,
                employeeId: currentEmployeeId,
              },
              onCompleted: (data) => {
                resetState();
                props.onRequestClose();
              },
            });
          }}
          title={
            <FormattedMessage
              defaultMessage="Send & mark as replied"
              description="TodoEditFeedbackPopover:SendReplyButton:title"
            />
          }
        />
      </Box>
    </>
  );
}
